
import { useState, useEffect, useMemo } from 'react';

interface AvailabilitySlot {
  id: string;
  date: string;
  time_slot: string;
  is_available: boolean;
}

interface BookingCalendarProps {
  availableSlots: AvailabilitySlot[];
  selectedDate: string | null;
  selectedTime: string | null;
  onSelectDate: (date: string) => void;
  onSelectTime: (time: string) => void;
}

export default function BookingCalendar({
  availableSlots,
  selectedDate,
  selectedTime,
  onSelectDate,
  onSelectTime,
}: BookingCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Initialise calendar to the first day of the current month
  useEffect(() => {
    const today = new Date();
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  }, []);

  // Set of dates that have at least one available slot
  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    availableSlots.forEach((slot) => {
      if (slot.is_available) dates.add(slot.date);
    });
    return dates;
  }, [availableSlots]);

  // List of time slots for the currently selected date
  const timeSlotsForDate = useMemo(() => {
    if (!selectedDate) return [];
    return availableSlots
      .filter((s) => s.date === selectedDate && s.is_available)
      .sort((a, b) => a.time_slot.localeCompare(b.time_slot));
  }, [availableSlots, selectedDate]);

  // Helpers for rendering the month grid
  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();

  const monthName = currentMonth.toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  });

  const goToPrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  // Reset time to midnight for accurate "past" comparisons
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Format time strings (e.g., "14:30" → "2:30 PM")
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayH = h % 12 || 12;
    return `${displayH}:${minutes} ${ampm}`;
  };

  return (
    <div className="space-y-6">
      {/* Calendar */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={goToPrevMonth}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl text-slate-700"></i>
          </button>
          <h4 className="text-lg font-semibold text-slate-900">{monthName}</h4>
          <button
            onClick={goToNextMonth}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl text-slate-700"></i>
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div
              key={day}
              className="text-center text-xs font-medium text-gray-400 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells before the 1st day */}
          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <div key={`empty-${i}`} className="h-10" />
          ))}

          {/* Actual days */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${currentMonth.getFullYear()}-${String(
              currentMonth.getMonth() + 1
            ).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dateObj = new Date(
              currentMonth.getFullYear(),
              currentMonth.getMonth(),
              day
            );
            const isPast = dateObj < today;
            const isAvailable = availableDates.has(dateStr);
            const isSelected = selectedDate === dateStr;
            const isToday = dateObj.getTime() === today.getTime();

            return (
              <button
                key={day}
                disabled={isPast || !isAvailable}
                onClick={() => onSelectDate(dateStr)}
                className={`h-10 rounded-xl text-sm font-medium transition-all cursor-pointer
                  ${isSelected
                    ? 'bg-emerald-500 text-white shadow-md shadow-emerald-200'
                    : isAvailable && !isPast
                    ? 'text-slate-900 hover:bg-emerald-50 hover:text-emerald-600'
                    : 'text-gray-300 cursor-not-allowed'
                  }
                  ${isToday && !isSelected ? 'ring-2 ring-emerald-300' : ''}
                `}
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
            <span className="text-xs text-gray-500">Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-200"></div>
            <span className="text-xs text-gray-500">Unavailable</span>
          </div>
        </div>
      </div>

      {/* Time slots for the selected date */}
      {selectedDate && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h4 className="text-sm font-semibold text-slate-900 mb-4">
            Available Times for{' '}
            <span className="text-emerald-600">
              {new Date(selectedDate + 'T00:00:00').toLocaleDateString(
                'en-US',
                {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                }
              )}
            </span>
          </h4>
          {timeSlotsForDate.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              {timeSlotsForDate.map((slot) => (
                <button
                  key={slot.id}
                  onClick={() => onSelectTime(slot.time_slot)}
                  className={`py-3 px-4 rounded-xl text-sm font-medium transition-all cursor-pointer
                    ${selectedTime === slot.time_slot
                      ? 'bg-emerald-500 text-white shadow-md shadow-emerald-200'
                      : 'bg-gray-50 text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 border border-gray-100'
                    }
                  `}
                >
                  {formatTime(slot.time_slot)}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              No available time slots for this date.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
